import { Injectable, inject } from '@angular/core';
import { ActivitiesGateway } from '@wilson/api/gateway';
import {
  Activity,
  ActivityCategoriesIdsMap,
  BulkUpdateActivitiesDto,
  OperationStatus,
  WilsonApp,
} from '@wilson/interfaces';
import { ShiftsService } from '@wilson/shifts';
import {
  addDays,
  addMinutes,
  differenceInCalendarDays,
  setHours,
  setMinutes,
  setSeconds,
} from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { SimplifiedShiftBreakOption } from '../helpers/is-simple-shift-activities';
import { SimplifiedShiftDrawerFormValue } from '../simplified-shift-drawer-form.service';
import {
  SimpleShiftActivity,
  sortActivitiesForSimpleShift,
} from '../helpers/is-simple-shift-activities';

enum OperationType {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}

type ActivityOperation =
  | { type: OperationType.Create; activity: Activity }
  | { type: OperationType.Update; activity: BulkUpdateActivitiesDto }
  | { type: OperationType.Delete; activity: { id: string } };

export interface SimplifiedActivitySaveInput {
  shiftId: string;
  previousShiftStartDate: string;
  activities: SimpleShiftActivity[];
  formValue: SimplifiedShiftDrawerFormValue;
  applyAgreement: boolean;
}

@Injectable()
export class SimplifiedShiftActivityOperationsService {
  private readonly activitiesGateway = inject(ActivitiesGateway);
  private readonly shiftsService = inject(ShiftsService);

  async saveSimpleShiftActivities(
    input: SimplifiedActivitySaveInput,
  ): Promise<{ startDate: string } | null> {
    const sortedActivities = sortActivitiesForSimpleShift(input.activities);
    if (!sortedActivities.length) {
      return null;
    }

    const { startDateTime, endDateTime, shiftStartDate } =
      this.resolveShiftWindow(input);

    if (
      !this.canApplyBreakOption({
        startDateTime,
        endDateTime,
        breakOption: input.formValue.breakOption,
        breakStartTime: input.formValue.breakStartTime,
        breakEndTime: input.formValue.breakEndTime,
      })
    ) {
      return null;
    }

    const agreementId = input.applyAgreement
      ? input.formValue.agreementId
      : null;
    const shouldApplyAgreement = input.applyAgreement;
    const locationId = input.formValue.location?.id ?? null;
    const activityCategoryId = input.formValue.activityCategoryId;

    const operations =
      input.formValue.breakOption == null
        ? this.generateSingleActivityOperations({
            existingActivities: sortedActivities,
            startDateTime,
            endDateTime,
            activityCategoryId,
            locationId,
            agreementId,
            shouldApplyAgreement,
          })
        : this.generateBreakActivityOperations({
            existingActivities: sortedActivities,
            startDateTime,
            endDateTime,
            activityCategoryId,
            locationId,
            agreementId,
            shouldApplyAgreement,
            breakOption: input.formValue.breakOption,
            breakStartTime: input.formValue.breakStartTime,
            breakEndTime: input.formValue.breakEndTime,
          });

    if (!operations.length) {
      return null;
    }

    await this.executeOperations(operations);
    return { startDate: shiftStartDate };
  }

  async applyDayDeltaToAllActivities(
    activities: SimpleShiftActivity[],
    previousShiftStartDate: string,
    nextShiftDate: Date,
    shiftId: string,
  ): Promise<void> {
    const dayDelta = differenceInCalendarDays(
      nextShiftDate,
      new Date(previousShiftStartDate),
    );
    if (!dayDelta || !activities.length) {
      return;
    }

    const updates: BulkUpdateActivitiesDto[] = activities
      .filter((activity) => !!activity.id)
      .map((activity) => ({
        id: activity.id as string,
        data: {
          startDatetime: addDays(
            new Date(activity.startDatetime),
            dayDelta,
          ).toISOString(),
          endDatetime: addDays(
            new Date(activity.endDatetime),
            dayDelta,
          ).toISOString(),
        },
      }));

    if (updates.length) {
      await firstValueFrom(
        this.activitiesGateway.bulkUpdateActivities(updates),
      );
    }

    await firstValueFrom(
      this.shiftsService.updateShift({
        id: shiftId,
        startDate: this.toDateOnly(nextShiftDate),
      }),
    );
  }

  private resolveShiftWindow(input: SimplifiedActivitySaveInput): {
    startDateTime: Date;
    endDateTime: Date;
    shiftStartDate: string;
  } {
    const sortedActivities = sortActivitiesForSimpleShift(input.activities);
    const formValue = input.formValue;
    const shiftDate = formValue.shiftDate
      ? new Date(formValue.shiftDate)
      : new Date(input.previousShiftStartDate);

    const baseStart = new Date(sortedActivities[0].startDatetime);
    const baseEnd = new Date(
      sortedActivities[sortedActivities.length - 1].endDatetime,
    );

    const startTimeSource = formValue.startTime ?? baseStart;
    const endTimeSource = formValue.endTime ?? baseEnd;

    const startDateTime = setSeconds(
      setMinutes(
        setHours(new Date(shiftDate), startTimeSource.getHours()),
        startTimeSource.getMinutes(),
      ),
      0,
    );

    let endDateTime = setSeconds(
      setMinutes(
        setHours(new Date(shiftDate), endTimeSource.getHours()),
        endTimeSource.getMinutes(),
      ),
      0,
    );

    // Keep overnight spans: end earlier than/equal to start means next calendar day.
    if (endDateTime.getTime() <= startDateTime.getTime()) {
      endDateTime = addDays(endDateTime, 1);
    }

    return {
      startDateTime,
      endDateTime,
      shiftStartDate: this.toDateOnly(shiftDate),
    };
  }

  private generateSingleActivityOperations(params: {
    existingActivities: SimpleShiftActivity[];
    startDateTime: Date;
    endDateTime: Date;
    activityCategoryId: string | null;
    locationId: string | null;
    agreementId: string | null;
    shouldApplyAgreement: boolean;
  }): ActivityOperation[] {
    const {
      existingActivities,
      startDateTime,
      endDateTime,
      activityCategoryId,
      locationId,
      agreementId,
      shouldApplyAgreement,
    } = params;
    const operations: ActivityOperation[] = [];

    operations.push({
      type: OperationType.Update,
      activity: {
        id: existingActivities[0].id as string,
        data: {
          activityCategoryId:
            activityCategoryId ?? existingActivities[0].activityCategoryId,
          startDatetime: startDateTime.toISOString(),
          endDatetime: endDateTime.toISOString(),
          startLocationId: locationId ?? existingActivities[0].startLocationId,
          endLocationId: locationId ?? existingActivities[0].endLocationId,
          ...(shouldApplyAgreement ? { agreementId } : {}),
        },
      },
    });

    existingActivities.slice(1).forEach((activity) => {
      if (activity.id) {
        operations.push({
          type: OperationType.Delete,
          activity: { id: activity.id },
        });
      }
    });

    return operations;
  }

  private generateBreakActivityOperations(params: {
    existingActivities: SimpleShiftActivity[];
    startDateTime: Date;
    endDateTime: Date;
    activityCategoryId: string | null;
    locationId: string | null;
    agreementId: string | null;
    shouldApplyAgreement: boolean;
    breakOption: Exclude<SimplifiedShiftBreakOption, null>;
    breakStartTime: Date | null;
    breakEndTime: Date | null;
  }): ActivityOperation[] {
    const {
      existingActivities,
      startDateTime,
      endDateTime,
      activityCategoryId,
      locationId,
      agreementId,
      shouldApplyAgreement,
      breakOption,
      breakStartTime,
      breakEndTime,
    } = params;

    const { breakStart, breakEnd } = this.resolveBreakWindow(
      startDateTime,
      endDateTime,
      breakOption,
      breakStartTime,
      breakEndTime,
    );

    if (
      !this.isBreakStrictlyInsideShift(
        startDateTime,
        endDateTime,
        breakStart,
        breakEnd,
      )
    ) {
      return [];
    }

    const resolvedCategoryId =
      activityCategoryId ?? existingActivities[0].activityCategoryId;
    const resolvedLocationId =
      locationId ?? existingActivities[0].startLocationId;
    const agreementUpdatePayload = shouldApplyAgreement ? { agreementId } : {};
    const agreementIdForCreate = shouldApplyAgreement
      ? agreementId
      : (existingActivities[0].agreementId ?? null);
    const shiftId = existingActivities[0].shiftId;

    const workActivities = existingActivities.filter(
      (activity) =>
        activity.activityCategoryId !== ActivityCategoriesIdsMap.Break,
    );
    const breakActivities = existingActivities.filter(
      (activity) =>
        activity.activityCategoryId === ActivityCategoriesIdsMap.Break,
    );

    const firstWorkActivity = workActivities[0] ?? existingActivities[0];
    const secondWorkActivity = workActivities.find(
      (activity) => activity.id !== firstWorkActivity.id,
    );
    const primaryBreakActivity = breakActivities[0];

    const operations: ActivityOperation[] = [
      {
        type: OperationType.Update,
        activity: {
          id: firstWorkActivity.id as string,
          data: {
            activityCategoryId: resolvedCategoryId,
            startDatetime: startDateTime.toISOString(),
            endDatetime: breakStart.toISOString(),
            startLocationId: resolvedLocationId,
            endLocationId: resolvedLocationId,
            ...agreementUpdatePayload,
            shiftId,
          },
        },
      },
    ];

    const retainedIds = new Set<string>([firstWorkActivity.id as string]);

    if (primaryBreakActivity?.id) {
      retainedIds.add(primaryBreakActivity.id);
      operations.push({
        type: OperationType.Update,
        activity: {
          id: primaryBreakActivity.id,
          data: {
            activityCategoryId: ActivityCategoriesIdsMap.Break,
            startDatetime: breakStart.toISOString(),
            endDatetime: breakEnd.toISOString(),
            startLocationId: resolvedLocationId,
            endLocationId: resolvedLocationId,
            ...agreementUpdatePayload,
            shiftId,
          },
        },
      });
    } else {
      operations.push({
        type: OperationType.Create,
        activity: this.createActivity({
          activityCategoryId: ActivityCategoriesIdsMap.Break,
          startDatetime: breakStart.toISOString(),
          endDatetime: breakEnd.toISOString(),
          startLocationId: resolvedLocationId,
          endLocationId: resolvedLocationId,
          agreementId: agreementIdForCreate,
          shiftId,
        }),
      });
    }

    if (secondWorkActivity?.id) {
      retainedIds.add(secondWorkActivity.id);
      operations.push({
        type: OperationType.Update,
        activity: {
          id: secondWorkActivity.id,
          data: {
            activityCategoryId: resolvedCategoryId,
            startDatetime: breakEnd.toISOString(),
            endDatetime: endDateTime.toISOString(),
            startLocationId: resolvedLocationId,
            endLocationId: resolvedLocationId,
            ...agreementUpdatePayload,
            shiftId,
          },
        },
      });
    } else {
      operations.push({
        type: OperationType.Create,
        activity: this.createActivity({
          activityCategoryId: resolvedCategoryId,
          startDatetime: breakEnd.toISOString(),
          endDatetime: endDateTime.toISOString(),
          startLocationId: resolvedLocationId,
          endLocationId: resolvedLocationId,
          agreementId: agreementIdForCreate,
          shiftId,
        }),
      });
    }

    existingActivities.forEach((activity) => {
      if (activity.id && !retainedIds.has(activity.id)) {
        operations.push({
          type: OperationType.Delete,
          activity: { id: activity.id },
        });
      }
    });

    return operations;
  }

  private canApplyBreakOption(params: {
    startDateTime: Date;
    endDateTime: Date;
    breakOption: SimplifiedShiftBreakOption;
    breakStartTime: Date | null;
    breakEndTime: Date | null;
  }): boolean {
    const { startDateTime, endDateTime, breakOption } = params;
    if (breakOption == null) {
      return true;
    }

    const shiftDurationMinutes =
      (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60);

    if (breakOption === 30 || breakOption === 60) {
      return shiftDurationMinutes > breakOption;
    }

    const { breakStart, breakEnd } = this.resolveBreakWindow(
      startDateTime,
      endDateTime,
      breakOption,
      params.breakStartTime,
      params.breakEndTime,
    );

    return this.isBreakStrictlyInsideShift(
      startDateTime,
      endDateTime,
      breakStart,
      breakEnd,
    );
  }

  private isBreakStrictlyInsideShift(
    startDateTime: Date,
    endDateTime: Date,
    breakStart: Date,
    breakEnd: Date,
  ): boolean {
    return (
      breakStart.getTime() > startDateTime.getTime() &&
      breakEnd.getTime() < endDateTime.getTime() &&
      breakEnd.getTime() > breakStart.getTime()
    );
  }

  private resolveBreakWindow(
    startDateTime: Date,
    endDateTime: Date,
    breakOption: Exclude<SimplifiedShiftBreakOption, null>,
    breakStartTime: Date | null,
    breakEndTime: Date | null,
  ): { breakStart: Date; breakEnd: Date } {
    if (breakOption === 'exact' && breakStartTime && breakEndTime) {
      let breakStart = setSeconds(
        setMinutes(
          setHours(new Date(startDateTime), breakStartTime.getHours()),
          breakStartTime.getMinutes(),
        ),
        0,
      );
      let breakEnd = setSeconds(
        setMinutes(
          setHours(new Date(startDateTime), breakEndTime.getHours()),
          breakEndTime.getMinutes(),
        ),
        0,
      );

      if (breakStart.getTime() < startDateTime.getTime()) {
        breakStart = addDays(breakStart, 1);
      }
      if (breakEnd.getTime() <= breakStart.getTime()) {
        breakEnd = addDays(breakEnd, 1);
      }

      return { breakStart, breakEnd };
    }

    const durationMinutes = breakOption === 30 ? 30 : 60;
    const totalShiftDurationInMinutes =
      (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60);
    const minutesUntilBreakStart =
      (totalShiftDurationInMinutes - durationMinutes) / 2;
    const breakStart = addMinutes(startDateTime, minutesUntilBreakStart);
    const breakEnd = addMinutes(breakStart, durationMinutes);
    return { breakStart, breakEnd };
  }

  private async executeOperations(
    operations: ActivityOperation[],
  ): Promise<void> {
    const creates: Activity[] = [];
    const updates: BulkUpdateActivitiesDto[] = [];
    const deletes: string[] = [];

    operations.forEach((operation) => {
      switch (operation.type) {
        case OperationType.Create:
          creates.push(operation.activity);
          break;
        case OperationType.Update:
          updates.push(operation.activity);
          break;
        case OperationType.Delete:
          deletes.push(operation.activity.id);
          break;
      }
    });

    if (updates.length) {
      await firstValueFrom(
        this.activitiesGateway.bulkUpdateActivities(updates),
      );
    }
    if (creates.length) {
      await firstValueFrom(this.activitiesGateway.createActivities(creates));
    }
    if (deletes.length) {
      await firstValueFrom(
        this.activitiesGateway.bulkDeleteActivities(deletes),
      );
    }
  }

  private createActivity(partial: Partial<Activity>): Activity {
    return {
      activityCategoryId: null,
      createdFrom: WilsonApp.Portal,
      endDatetime: '',
      endLocationId: null,
      name: '',
      operationalStatus: OperationStatus.NotStarted,
      serviceId: null,
      shiftId: null,
      startDatetime: '',
      startLocationId: null,
      viaLocationIds: [],
      cargo: null,
      track: null,
      note: null,
      orderNumber: null,
      wagonNumber: null,
      locomotiveNumber: null,
      externalNumber: null,
      priority: null,
      travelDirection: null,
      tonnage: null,
      numberOfWagons: null,
      agreementId: null,
      jobId: null,
      vehicleEmissionClassId: null,
      professionId: null,
      organizationalUnitTransportGoodsId: null,
      ...partial,
    };
  }

  private toDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
